<template>
  <section
    v-if="isAdaptivePresentation"
    class="post-presentation-preview"
    :class="`post-presentation-preview--${presentationMode}`"
    :data-presentation-mode="presentationMode"
    role="region"
    :aria-label="`${presentationLabel} post preview`"
  >
    <div
      v-if="isStory"
      class="post-presentation-preview__story"
      :class="{
        'post-presentation-preview__story--cover': storyUsesMobileCover,
      }"
    >
      <div
        v-if="storyMedia"
        class="post-presentation-preview__story-stage"
      >
        <video
          v-if="storyMedia.type === 'video'"
          :src="storyMedia.url"
          :poster="storyMedia.poster || undefined"
          class="post-presentation-preview__story-media"
          controls
          playsinline
          preload="metadata"
          :aria-label="mediaLabel(storyMedia, 0)"
          @loadedmetadata="recordVideoRatio(storyMedia.url, $event)"
        ></video>
        <img
          v-else
          :src="storyMedia.url"
          :alt="mediaLabel(storyMedia, 0)"
          class="post-presentation-preview__story-media"
          decoding="async"
          @load="recordImageRatio(storyMedia.url, $event)"
        />
      </div>

      <header
        v-if="displayTitle"
        class="post-presentation-preview__story-title"
      >
        <h2>{{ displayTitle }}</h2>
      </header>

      <div
        v-if="authoredBody"
        class="post-presentation-preview__story-body"
      >
        <p>{{ authoredBody }}</p>
      </div>
    </div>

    <div
      v-else-if="isGallery"
      class="post-presentation-preview__gallery"
    >
      <header
        v-if="displayTitle"
        class="post-presentation-preview__gallery-title"
      >
        <h2>{{ displayTitle }}</h2>
      </header>

      <div class="post-presentation-preview__gallery-scroll">
        <div
          v-if="authoredBody"
          class="post-presentation-preview__gallery-body"
        >
          <p>{{ authoredBody }}</p>
        </div>

        <figure
          v-for="(media, mediaIndex) in visualMedia"
          :key="`${media.url}-${mediaIndex}`"
          class="post-presentation-preview__gallery-item"
          :data-gallery-index="mediaIndex"
        >
          <video
            v-if="media.type === 'video'"
            :src="media.url"
            :poster="media.poster || undefined"
            class="post-presentation-preview__gallery-media"
            controls
            playsinline
            preload="metadata"
            :aria-label="mediaLabel(media, mediaIndex)"
            @loadedmetadata="recordVideoRatio(media.url, $event)"
          ></video>
          <img
            v-else
            :src="media.url"
            :alt="mediaLabel(media, mediaIndex)"
            class="post-presentation-preview__gallery-media"
            loading="lazy"
            decoding="async"
            @load="recordImageRatio(media.url, $event)"
          />
          <figcaption
            v-if="media.caption"
            class="post-presentation-preview__caption"
          >
            {{ media.caption }}
          </figcaption>
        </figure>
      </div>
    </div>

    <article
      v-else-if="isArticle"
      class="post-presentation-preview__article"
    >
      <h2
        v-if="displayTitle"
        class="post-presentation-preview__article-title"
      >
        {{ displayTitle }}
      </h2>

      <div
        v-if="visualMedia.length"
        class="post-presentation-preview__article-media-list"
      >
        <figure
          v-for="(media, mediaIndex) in visualMedia"
          :key="`${media.url}-${mediaIndex}`"
          class="post-presentation-preview__article-media-item"
        >
          <video
            v-if="media.type === 'video'"
            :src="media.url"
            :poster="media.poster || undefined"
            class="post-presentation-preview__article-media"
            controls
            playsinline
            preload="metadata"
            :aria-label="mediaLabel(media, mediaIndex)"
            @loadedmetadata="recordVideoRatio(media.url, $event)"
          ></video>
          <img
            v-else
            :src="media.url"
            :alt="mediaLabel(media, mediaIndex)"
            class="post-presentation-preview__article-media"
            loading="lazy"
            decoding="async"
            @load="recordImageRatio(media.url, $event)"
          />
          <figcaption
            v-if="media.caption"
            class="post-presentation-preview__article-caption"
          >
            {{ media.caption }}
          </figcaption>
        </figure>
      </div>

      <p
        v-if="authoredBody"
        class="post-presentation-preview__article-body"
      >
        {{ authoredBody }}
      </p>
    </article>
  </section>
</template>

<script setup>
import { computed, reactive } from "vue";
import {
  PRESENTATION_MODES,
  getAuthoredBody,
  getVisualMedia,
  resolvePresentationMode,
  shouldCoverStoryMedia,
} from "@/utils/postPresentation";

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
});

const normalizeText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    return normalizeText(value.text || value.value || value.name || "");
  }
  return "";
};

const normalizeUrl = (value) =>
  typeof value === "string" ? value.trim() : "";

const presentationMode = computed(() =>
  resolvePresentationMode(props.item),
);
const isStory = computed(
  () => presentationMode.value === PRESENTATION_MODES.STORY,
);
const isGallery = computed(
  () => presentationMode.value === PRESENTATION_MODES.GALLERY,
);
const isArticle = computed(
  () => presentationMode.value === PRESENTATION_MODES.ARTICLE,
);
const isAdaptivePresentation = computed(
  () => isStory.value || isGallery.value || isArticle.value,
);
const presentationLabel = computed(() => {
  if (isArticle.value) return "Article";
  if (isGallery.value) return "Gallery";
  return "Story";
});

const displayTitle = computed(() =>
  normalizeText(
    props.item?.itemName || props.item?.title || props.item?.name,
  ),
);
const authoredBody = computed(() => getAuthoredBody(props.item));

const visualMedia = computed(() => {
  const item = props.item || {};
  const sourceImages = Array.isArray(item.images) ? item.images : [];
  const captions = Array.isArray(item.imageCaptions)
    ? item.imageCaptions
    : [];
  const mediaThumbnails = Array.isArray(item.mediaThumbnails)
    ? item.mediaThumbnails
    : [];
  const isStandaloneVideo =
    normalizeText(item.mediaType).toLowerCase() === "video";

  const enrichedMedia = getVisualMedia(item).map((media, visualIndex) => {
    const sourceIndex = sourceImages.findIndex(
      (sourceUrl) => normalizeUrl(sourceUrl) === media.url,
    );
    const captionIndex = isStandaloneVideo
      ? 0
      : sourceIndex >= 0
        ? sourceIndex
        : visualIndex;
    const poster =
      media.poster ||
      (sourceIndex >= 0
        ? normalizeUrl(mediaThumbnails[sourceIndex])
        : "");

    return {
      ...media,
      ...(media.type === "video" && poster ? { poster } : {}),
      caption: normalizeText(captions[captionIndex]),
      sourceIndex,
    };
  });

  if (enrichedMedia.length > 1) {
    const thumbnailIndex = Number.isInteger(item.thumbnailIndex)
      ? item.thumbnailIndex
      : 0;
    const thumbnailPosition = enrichedMedia.findIndex(
      (media) => media.sourceIndex === thumbnailIndex,
    );

    if (thumbnailPosition > 0) {
      const [thumbnailMedia] = enrichedMedia.splice(thumbnailPosition, 1);
      enrichedMedia.unshift(thumbnailMedia);
    }
  }

  return enrichedMedia.map(({ sourceIndex, ...media }) => media);
});

const storyMedia = computed(() => visualMedia.value[0] || null);
const mediaAspectByUrl = reactive(new Map());

const recordRatio = (url, width, height) => {
  if (!url || !Number.isFinite(width) || !Number.isFinite(height)) return;
  if (width <= 0 || height <= 0) return;
  mediaAspectByUrl.set(url, width / height);
};

const recordImageRatio = (url, event) => {
  const image = event?.currentTarget;
  recordRatio(url, image?.naturalWidth, image?.naturalHeight);
};

const recordVideoRatio = (url, event) => {
  const video = event?.currentTarget;
  recordRatio(url, video?.videoWidth, video?.videoHeight);
};

const storyUsesMobileCover = computed(() => {
  const url = storyMedia.value?.url;
  if (!url) return false;
  return shouldCoverStoryMedia(mediaAspectByUrl.get(url), true);
});

const mediaLabel = (media, mediaIndex) =>
  media.caption ||
  displayTitle.value ||
  `Post media ${mediaIndex + 1}`;
</script>

<style scoped>
.post-presentation-preview {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--color-media-canvas);
  color: #fff;
  text-align: left;
}

.post-presentation-preview *,
.post-presentation-preview *::before,
.post-presentation-preview *::after {
  box-sizing: border-box;
}

.post-presentation-preview h2,
.post-presentation-preview p,
.post-presentation-preview figure {
  margin: 0;
}

.post-presentation-preview__story,
.post-presentation-preview__story-stage,
.post-presentation-preview__gallery {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.post-presentation-preview__story-stage {
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-presentation-preview__story-media {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: var(--color-media-canvas);
}

.post-presentation-preview__story--cover
  .post-presentation-preview__story-media {
  object-fit: cover;
}

.post-presentation-preview__story-title,
.post-presentation-preview__gallery-title {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  padding: 20px 16px 34px;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.82),
    rgba(0, 0, 0, 0)
  );
  pointer-events: none;
}

.post-presentation-preview__story-title h2,
.post-presentation-preview__gallery-title h2 {
  overflow-wrap: anywhere;
  color: #fff;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.65);
}

.post-presentation-preview__story-body {
  position: absolute;
  right: 16px;
  bottom: 56px;
  left: 16px;
  z-index: 2;
  max-height: 22%;
  padding: 10px 14px;
  overflow-y: auto;
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  font-size: 15px;
  line-height: 1.45;
  overscroll-behavior: contain;
  white-space: pre-wrap;
}

.post-presentation-preview__gallery-title {
  z-index: 3;
}

.post-presentation-preview__gallery-scroll {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  padding: 88px 0 48px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.post-presentation-preview__gallery-body {
  width: calc(100% - 32px);
  margin: 0 auto;
  padding: 10px 14px;
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  font-size: 15px;
  line-height: 1.45;
  text-align: center;
  white-space: pre-wrap;
}

.post-presentation-preview__gallery-item {
  display: flex;
  width: 100%;
  min-height: 70%;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
}

.post-presentation-preview__gallery-media {
  display: block;
  width: 100%;
  height: auto;
  max-height: 78%;
  object-fit: contain;
  background: var(--color-media-canvas);
}

.post-presentation-preview__caption,
.post-presentation-preview__article-caption {
  width: 100%;
  padding: 8px 12px;
  overflow-wrap: anywhere;
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
  white-space: pre-wrap;
}

.post-presentation-preview__article {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 24px 16px 56px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--color-surface-canvas, #fff);
  color: var(--color-text-primary, #111);
  -webkit-overflow-scrolling: touch;
}

.post-presentation-preview__article-title {
  margin-bottom: 18px;
  overflow-wrap: anywhere;
  color: inherit;
  font-size: 32px;
  font-weight: 650;
  line-height: 1.15;
}

.post-presentation-preview__article-media-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 22px;
}

.post-presentation-preview__article-media-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.post-presentation-preview__article-media {
  display: block;
  width: 100%;
  height: auto;
  max-height: 520px;
  object-fit: contain;
  border-radius: calc(var(--radius-sm, 8px) / 2);
  background: var(--color-media-canvas);
}

.post-presentation-preview__article-caption {
  background: var(--color-media-caption-scrim);
  color: inherit;
}

.post-presentation-preview__article-body {
  overflow-wrap: anywhere;
  color: inherit;
  font-size: 17px;
  line-height: 1.65;
  white-space: pre-wrap;
}
</style>
